import { useData } from 'vike-react/useData';
import { useEffect, useMemo, useState } from 'react';
import type { Data } from './+data';
import { Container } from '../../../components/layout/Container';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { Combobox } from '../../../components/ui/combobox';
import { useStoredAudienceId } from '../../../hooks/useActiveAudience';
import type { AudienceId } from '@shared/constants/audiences';
import { cn } from '../../../lib/cn';
import { Search, SlidersHorizontal, Tractor } from 'lucide-react';

/* ──────────────────────── Types ──────────────────────────────── */
type Sector = 'ALL' | 'BAR' | 'BAT' | 'IND' | 'AGRI' | 'TRA' | 'RES';

const SECTOR_LABELS: Record<Sector, string> = {
  ALL: 'Tous les secteurs',
  BAR: 'Résidentiel (BAR)',
  BAT: 'Tertiaire (BAT)',
  IND: 'Industrie (IND)',
  AGRI: 'Agriculture (AGRI)',
  TRA: 'Transport (TRA)',
  RES: 'Réseaux (RES)',
};

const SECTOR_COLORS: Record<Sector, string> = {
  ALL: 'bg-stone-100 text-stone-700 border-stone-200',
  BAR: 'bg-brand-50 text-brand-700 border-brand-200',
  BAT: 'bg-lavender-50 text-lavender-700 border-lavender-200',
  IND: 'bg-amber-50 text-amber-700 border-amber-200',
  AGRI: 'bg-accent-50 text-accent-700 border-accent-200',
  TRA: 'bg-blue-50 text-blue-700 border-blue-200',
  RES: 'bg-stone-50 text-stone-600 border-stone-200',
};

const SECTOR_LABELS_FULL: Record<Sector, string> = {
  ALL: 'Tous les secteurs',
  BAR: 'Résidentiel',
  BAT: 'Tertiaire',
  IND: 'Industrie',
  AGRI: 'Agriculture',
  TRA: 'Transport',
  RES: 'Réseaux',
};

const PROFILE_LABELS: Record<string, string> = {
  PRO: 'Professionnel',
  ENTREPRISE: 'Grande entreprise',
  PARTICULIER: 'Particulier',
  SYNDIC: 'Syndic',
  COLLECTIVITE: 'Collectivité',
};

const PROFILE_COLORS: Record<string, string> = {
  PRO: 'bg-blue-50 text-blue-700 border-blue-200',
  ENTREPRISE: 'bg-lavender-50 text-lavender-700 border-lavender-200',
  PARTICULIER: 'bg-brand-50 text-brand-700 border-brand-200',
  SYNDIC: 'bg-stone-100 text-stone-700 border-stone-200',
  COLLECTIVITE: 'bg-amber-50 text-amber-700 border-amber-200',
};

/* ──────────────────────── Composant ──────────────────────────── */

function deriveSectorFromAudience(audience: AudienceId | null): Sector {
  if (audience === 'agriculteurs') return 'AGRI';
  if (audience === 'particuliers') return 'BAR';
  if (audience === 'entreprises' || audience === 'collectivites') return 'BAT';
  return 'ALL';
}

export default function CeeFichesPage() {
  const { fiches } = useData<Data>();
  const audience = useStoredAudienceId();

  // SSR-safe : on part toujours sur 'ALL' au render initial pour éviter
  // tout hydration mismatch. Le useEffect ci-dessous applique le filtre
  // dérivé de l'audience une fois côté client. Si l'utilisateur clique
  // explicitement sur un secteur, son choix est préservé.
  const [activeSector, setActiveSector] = useState<Sector>('ALL');
  const [userOverrode, setUserOverrode] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (userOverrode) return;
    setActiveSector(deriveSectorFromAudience(audience));
  }, [audience, userOverrode]);

  const handleSectorClick = (s: Sector) => {
    setUserOverrode(true);
    setActiveSector(s);
  };

  const filteredFiches = useMemo(() => {
    const q = query.toLowerCase();
    return fiches.filter((f) => {
      const matchSector = activeSector === 'ALL' || f.sector === activeSector;
      const matchQuery =
        !q ||
        f.code.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q);
      return matchSector && matchQuery;
    });
  }, [fiches, activeSector, query]);

  const sectors: Sector[] = ['ALL', 'BAR', 'BAT', 'IND', 'AGRI', 'TRA', 'RES'];

  return (
    <div className="min-h-screen bg-stone-50">
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'CEE', url: 'https://solutionecofrance.fr/cee' },
          { name: 'Fiches', url: 'https://solutionecofrance.fr/cee/fiches' },
        ]}
      />

      {/* ── Bandeau agri ── */}
      {audience === 'agriculteurs' && (
        <div className="border-b border-accent-200 bg-accent-50 py-2.5">
          <Container>
            <div className="flex items-center gap-2 text-sm text-accent-800">
              <Tractor className="h-4 w-4 text-accent-600" />
              <span>
                Filtre <strong>Agriculture (AGRI)</strong> pré-appliqué — fiches CEE AGRI-EQ pour
                exploitations agricoles.
              </span>
            </div>
          </Container>
        </div>
      )}

      <Container className="py-10">
        {/* ── Header ── */}
        <div className="mb-8">
          <p className="eyebrow">Référentiel</p>
          <h1 className="mt-2 h-hero">
            Catalogue des fiches CEE
          </h1>
          <p className="mt-3 max-w-2xl text-stone-600">
            {fiches.length} fiches officielles, source Ministère de la Transition écologique.
            Filtrez par secteur ou utilisez la recherche.
          </p>
        </div>

        {/* ── Layout Stripe Docs (sidebar + content) ── */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          {/* Sidebar filtres */}
          <aside className="lg:sticky lg:top-20 lg:w-52 lg:shrink-0">
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Secteur
            </p>
            <nav className="flex flex-row flex-wrap gap-2 lg:flex-col lg:gap-1">
              {sectors.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSectorClick(s)}
                  className={cn(
                    'flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-100',
                    activeSector === s
                      ? SECTOR_COLORS[s]
                      : 'border-transparent bg-transparent text-stone-600 hover:bg-stone-100',
                  )}
                >
                  {SECTOR_LABELS[s]}
                  {s !== 'ALL' && (
                    <span className="ml-auto text-xs text-stone-400">
                      {fiches.filter((f) => f.sector === s).length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Search */}
            <div className="mb-6">
              <Combobox
                mode="inline"
                placeholder="Rechercher une fiche (code, intitulé, mot-clé…)"
                items={fiches.map((f) => ({
                  id: f.code,
                  label: f.label,
                  sublabel: f.code,
                  href: `/cee/fiches/${f.code}`,
                }))}
                onQueryChange={setQuery}
                className="w-full"
              />
            </div>

            {/* Résultats */}
            {filteredFiches.length === 0 ? (
              <div className="py-16 text-center text-stone-500">
                <Search className="mx-auto mb-3 h-8 w-8 text-stone-300" />
                <p className="font-medium">Aucune fiche trouvée</p>
                <p className="mt-1 text-sm">Modifiez le filtre ou la recherche.</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-subtle">
                {filteredFiches.map((fiche, idx) => (
                  <a
                    key={fiche.code}
                    href={`/cee/fiches/${fiche.code}`}
                    className={cn(
                      'group flex items-start gap-4 px-5 py-4 transition-colors duration-100 hover:bg-stone-50',
                      idx !== 0 && 'border-t border-stone-100',
                    )}
                  >
                    {/* Code badge */}
                    <span className="mt-0.5 shrink-0 rounded-md bg-stone-100 px-2 py-0.5 font-mono text-xs font-semibold text-stone-700 group-hover:bg-stone-200">
                      {fiche.code}
                    </span>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 font-medium text-ink group-hover:text-brand-700">
                        {fiche.label}
                      </p>
                      <p className="mt-0.5 line-clamp-1 text-sm text-stone-500">
                        {fiche.description}
                      </p>
                      {/* Tags profils éligibles */}
                      {(fiche.eligibleProfiles as string[]).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(fiche.eligibleProfiles as string[]).map((p) => (
                            <span
                              key={p}
                              className={cn(
                                'rounded-full border px-2 py-0.5 text-xs font-medium',
                                PROFILE_COLORS[p] ?? 'bg-stone-100 text-stone-600 border-stone-200',
                              )}
                            >
                              {PROFILE_LABELS[p] ?? p}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Secteur + prime */}
                    <div className="hidden shrink-0 flex-col items-end gap-1.5 sm:flex">
                      <span
                        className={cn(
                          'rounded-md border px-2 py-0.5 text-xs font-medium',
                          SECTOR_COLORS[fiche.sector as Sector] ?? SECTOR_COLORS.ALL,
                        )}
                      >
                        {SECTOR_LABELS_FULL[fiche.sector as Sector] ?? fiche.sector}
                      </span>
                      <span className="max-w-[140px] text-right text-xs leading-snug text-stone-400">
                        {fiche.primeEstimate}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            )}

            <p className="mt-4 text-right text-xs text-stone-400">
              {filteredFiches.length} fiche{filteredFiches.length !== 1 ? 's' : ''} affichée
              {filteredFiches.length !== 1 ? 's' : ''} sur {fiches.length}
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
}
